/**
 * Converts in-app Raven settings to Raven API settings.
 *
 * @param {RavenSettings} settings
 * @returns {RavenApiDeviceSettings}
 */
function settingsToRaven(settings) {
  return {
    audio: {
      audio_notifications_enabled: settings.audioNotificationsEnabled,
    },
    camera: {
      road_camera: {
        camera_enabled: settings.videoRecordingRoad,
        video_enabled: settings.videoRecordingRoad,
        audio_recording: settings.audioRecording,
      },
      cabin_camera: {
        camera_enabled: settings.videoRecordingCabin,
        video_enabled: settings.videoRecordingCabin,
        audio_recording: settings.audioRecording,
      },
    },
    display: {
      distance_unit: settings.distanceUnit,
      system_locale: settings.systemLocale,
      temperature_unit: settings.temperatureUnit,
    },
    driver_id: {
      barcode_driver_id_enabled: settings.driverIdEnabled,
      barcode_driver_id_request_period: 'PER_TRIP',
    },
    events: {
      cellphone_detection_event_enabled: settings.events.cellphoneDetectionEnabled,
      // eslint-disable-next-line max-len
      cellphone_detection_visual_alert_enabled: settings.events.cellphoneDetectionVisualAlertEnabled,
    },
  };
}

/**
 * Converts Raven API settings to in-app Raven settings.
 *
 * @param {RavenApiDeviceSettings} settings
 * @returns {RavenSettings}
 */
function ravenToSettings(ravenSettings, compact = false) {
  const { audio_recording: cabinAudio } = ravenSettings.camera?.cabin_camera || {};
  const { audio_recording: roadAudio } = ravenSettings.camera?.road_camera || {};
  const {
    barcode_driver_id_enabled: barcodeDriverIdEnabled,
    barcode_driver_id_request_period: barcodeDriverIdRequestPeriod,
  } = ravenSettings.driver_id || {};
  const { events } = ravenSettings;

  // Save value as "null" to indicate that the settings needs to be re-saved
  const audioRecording = cabinAudio !== roadAudio ? null : cabinAudio;

  // Save value as "null" to indicate that the settings needs to be re-saved
  const driverIdEnabled = barcodeDriverIdRequestPeriod !== 'PER_TRIP' ? null : barcodeDriverIdEnabled;

  const settings = {
    audioNotificationsEnabled: ravenSettings.audio?.audio_notifications_enabled,
    audioRecording,
    distanceUnit: ravenSettings.display?.distance_unit,
    driverIdEnabled,
    events: {
      cellphoneDetectionEnabled: events?.cellphone_detection_event_enabled,
      cellphoneDetectionVisualAlertEnabled: events?.cellphone_detection_visual_alert_enabled,
    },
    systemLocale: ravenSettings.display?.system_locale,
    temperatureUnit: ravenSettings.display?.temperature_unit,
    videoRecordingCabin: getCameraEnabled('cabin', ravenSettings),
    videoRecordingRoad: getCameraEnabled('road', ravenSettings),
  };

  return compact ? toCompact(settings) : settings;
}

function getCameraEnabled(type, ravenSettings) {
  const {
    camera_enabled: cameraEnabled,
    video_enabled: videoEnabled,
  } = ravenSettings.camera?.[`${type}_camera`] || {};
  return cameraEnabled !== videoEnabled ? null : cameraEnabled;
}

/**
 * Converts RavenSettings to a compact version.
 *
 * @param {RavenSettings} settings
 * @returns {RavenSettingsCompact}
 */
function toCompact(settings) {
  return {
    aNE: settings.audioNotificationsEnabled,
    aR: settings.audioRecording,
    dU: settings.distanceUnit,
    dIE: settings.driverIdEnabled,
    e: {
      cDE: settings.events?.cellphoneDetectionEnabled,
      cDVAE: settings.events?.cellphoneDetectionVisualAlertEnabled,
    },
    sL: settings.systemLocale,
    tU: settings.temperatureUnit,
    vRC: settings.videoRecordingCabin,
    vRR: settings.videoRecordingRoad,
  };
}

/**
 * Converts compact version of RavenSettings to the full version.
 *
 * @param {RavenSettingsCompact} settings
 * @returns {RavenSettings}
 */
function fromCompact(settings) {
  return {
    audioNotificationsEnabled: settings.aNE,
    audioRecording: settings.aR,
    distanceUnit: settings.dU,
    driverIdEnabled: settings.dIE,
    events: {
      cellphoneDetectionEnabled: settings.e?.cDE,
      cellphoneDetectionVisualAlertEnabled: settings.e?.cDVAE,
    },
    systemLocale: settings.sL,
    temperatureUnit: settings.tU,
    videoRecordingCabin: settings.vRC,
    videoRecordingRoad: settings.vRR,
  };
}

module.exports = {
  fromCompact,
  getCameraEnabled,
  ravenToSettings,
  settingsToRaven,
  toCompact,
};
